import React from "react";
import { api } from "@/lib/config";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Upload, X, Loader2 } from "lucide-react";

const PROJECT_TYPES = [
  "Flooring installation (hardwood)",
  "Flooring installation (laminate/vinyl)",
  "Tile flooring",
  "Tile — bathroom / shower",
  "Tile — kitchen backsplash",
  "Repairs & replacements",
  "Custom design work",
  "Other",
];

export default function QuoteRequest() {
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", address: "", project_type: "", description: "" });
  const [photos, setPhotos] = React.useState([]); // {path, url}
  const [uploading, setUploading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { path: r.data.path, url: URL.createObjectURL(file) }]);
      }
    } catch {
      toast.error("Upload failed. Please try a smaller image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotes", { ...form, photo_paths: photos.map((p) => p.path) });
      setDone(true);
      toast.success("Quote request sent — we&apos;ll be in touch within 1 business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="eyebrow text-[color:var(--brand-wood)]">Received</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl">Thank you.</h1>
        <p className="mt-4 text-[color:var(--brand-woodDark)]">
          Your request has been sent to our team. We&apos;ll reach out to you within one business day.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="max-w-2xl">
        <div className="eyebrow text-[color:var(--brand-wood)]">Request a Quote</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          Tell us about your project.
        </h1>
        <p className="mt-4 text-[color:var(--brand-woodDark)]">
          Share dimensions, materials, and photos — the more detail, the more accurate our estimate.
        </p>
      </div>

      <form onSubmit={submit} className="mt-12 grid gap-6 sm:grid-cols-2" data-testid="quote-form">
        <Field label="Full name" htmlFor="q-name">
          <Input id="q-name" required data-testid="quote-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/>
        </Field>
        <Field label="Phone" htmlFor="q-phone">
          <Input id="q-phone" required data-testid="quote-phone" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})}/>
        </Field>
        <Field label="Email" htmlFor="q-email">
          <Input id="q-email" type="email" required data-testid="quote-email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}/>
        </Field>
        <Field label="Address" htmlFor="q-address">
          <Input id="q-address" required data-testid="quote-address" value={form.address} onChange={(e)=>setForm({...form, address:e.target.value})}/>
        </Field>
        <Field label="Project type" htmlFor="q-type" className="sm:col-span-2">
          <Select value={form.project_type} onValueChange={(v)=>setForm({...form, project_type:v})}>
            <SelectTrigger data-testid="quote-project-type" id="q-type"><SelectValue placeholder="Choose a project type" /></SelectTrigger>
            <SelectContent>
              {PROJECT_TYPES.map((p)=>(<SelectItem key={p} value={p} data-testid={`opt-${p.slice(0,10)}`}>{p}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Description" htmlFor="q-desc" className="sm:col-span-2">
          <Textarea id="q-desc" required rows={5} data-testid="quote-description" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} placeholder="Approx. sq ft, existing floor, materials you like, timing..." />
        </Field>

        <div className="sm:col-span-2">
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-widest text-[color:var(--brand-woodDark)]">Photos (optional)</label>
            <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[color:var(--brand-woodDark)] hover:text-[color:var(--brand-ink)]">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin"/> : <Upload className="h-4 w-4"/>}
              {uploading ? "Uploading..." : "Add photos"}
              <input data-testid="quote-file-input" type="file" accept="image/*" multiple className="hidden" onChange={onFile} />
            </label>
          </div>
          {photos.length > 0 && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-black/10">
                  <img src={p.url} alt="upload preview" className="w-full h-full object-cover" />
                  <button type="button" onClick={()=>removePhoto(i)} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center">
                    <X className="h-3.5 w-3.5"/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sm:col-span-2 flex items-center justify-between mt-4">
          <p className="text-xs text-[color:var(--brand-woodDark)]">Submissions go to <span className="text-[color:var(--brand-ink)]">info@jfeva.com</span></p>
          <button
            type="submit"
            data-testid="quote-submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] px-6 py-3 text-sm font-medium disabled:opacity-70 hover:-translate-y-0.5 transition-transform"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : null}
            Submit request
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, htmlFor, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-widest text-[color:var(--brand-woodDark)]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
